import React, { useEffect, useState, useRef } from "react";

/**
 * A simplified audio wave visualization component
 * @param {Object} props - Component props
 * @param {React.RefObject} props.micRef - Reference to the audio element
 * @param {boolean} props.isLocal - Whether this is the local participant
 * @param {boolean} props.micOn - Whether the mic is on
 * @returns {JSX.Element} SimpleAudioWave component
 */
const SimpleAudioWave = ({ micRef, isLocal, micOn, isOnlyLocal }) => {
  const [bars, setBars] = useState([]);
  const intervalRef = useRef(null);
  const componentMounted = useRef(true);

  // Generate and update the audio bars
  useEffect(() => {
    // Only animate if mic is on
    if (!micOn) {
      const staticBars = Array(12)
        .fill(0)
        .map((_, i) => ({
          id: i,
          height: 10,
          animate: false,
        }));
      setBars(staticBars);
      return;
    }

    // Create animated bars
    const animatedBars = Array(12)
      .fill(0)
      .map((_, i) => ({
        id: i,
        height: Math.floor(Math.random() * 40) + 10,
        animate: true,
      }));
    setBars(animatedBars);

    // Update bars periodically if mic is on
    intervalRef.current = setInterval(() => {
      if (micOn && componentMounted.current) {
        setBars((prev) =>
          prev.map((bar) => ({
            ...bar,
            height: Math.floor(Math.random() * 40) + 10,
          }))
        );
      }
    }, 200);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [micOn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className="w-full flex flex-col items-center">
      {/* Audio visualization */}
      <div className="flex items-end justify-center gap-1 h-16 mb-4 px-4 py-2 bg-gray-100/50 rounded-full">
        {bars.map((bar) => (
          <div
            key={bar.id}
            className={`w-1 rounded-full transition-all duration-200 ${
              bar.animate ? "animate-pulse" : ""
            }`}
            style={{
              height: `${bar.height}px`,
              backgroundColor: isLocal ? "#A40165" : "#4CAF50",
              animationDelay: `${bar.id * 0.1}s`,
              opacity: bar.animate ? 0.8 : 0.3,
            }}
          />
        ))}
      </div>

      {/* Status text */}
      <p className="text-sm text-gray-500">
        {isOnlyLocal ? (
          <span>Joining ...</span>
        ) : (
          <span>
            Remote participant •{" "}
            {micOn ? "Their mic is on" : "Their mic is muted"}
          </span>
        )}
      </p>
    </div>
  );
};

export default SimpleAudioWave;
